<template>
  <Modal
    actions
    name="APPOINTMENT_CALCULATE_PAYMENT_MODAL"
    :title="$t('financial.updatePayments.calculated.modalTitle')"
    @modalOpened="preparePayment"
    :persistent="true"
  >
    <template v-slot:body>
      <v-form v-model="isValid" ref="calculateForm" lazy-validation>
        <p class="mb-2">
          {{ $t('financial.updatePayments.calculated.markRealty') }}
        </p>

        <v-card
          v-for="realty in payment.realties"
          :key="realty.id"
          elevation="0"
          class="px-5 py-3"
          outlined
        >
          <v-checkbox
            v-model="realty.done"
            :label="`${realty.code}: ${realty.address}`"
            hide-details="auto"
            class="mb-2"
            dense
          />

          <div v-if="!realty.done">
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="realty.itemReason"
                  :label="$t('financial.updatePayments.calculated.itemReason')"
                  :no-data-text="$t('appointment.filters.noData')"
                  :items="appointmentItemReasons"
                  :rules="[rules.required]"
                  :loading="isLoading"
                  item-text="description"
                  hide-details="auto"
                  item-value="id"
                  outlined
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-textarea
                  :label="$t('financial.updatePayments.calculated.description')"
                  v-model="realty.description"
                  :rules="[rules.required]"
                  hide-details="auto"
                  outlined
                  rows="3"
                />
              </v-col>
            </v-row>
          </div>
        </v-card>

        <v-card
          v-if="hasAnyRealtyDone"
          class="mt-4 px-5 py-3"
          outlined
        >
          <v-row>
            <v-col>
              <v-textarea
                v-model="payment.movementDescription"
                :label="$t('movement.label.comment')"
                :rules="[rules.required]"
                hide-details="auto"
                outlined
                rows="3"
              />
            </v-col>
          </v-row>
        </v-card>

        <v-card class="grey lighten-4 mt-5 pa-5" outlined>
          <v-row align="center">
            <v-col class="pt-0">
              <HCheckbox
                v-model="changeAdditional"
                :label="$t('financial.updatePayments.calculated.changeAdditional')"
              />
            </v-col>
            <v-col cols="1" class="pt-0">
              <v-icon color="grey" small>
                mdi-ray-start-arrow
              </v-icon>
            </v-col>
            <v-col cols="5" class="pt-0">
              <div
                v-for="additional in payment.additionals"
                :key="additional.id"
              >
                <v-text-field
                  v-model.lazy="additional.value"
                  v-money="catalogCurrency()"
                  :disabled="!changeAdditional"
                  :label="$t('appointment.reschedule.displacement')"
                  class="amount amount--small inputAmount"
                  hide-details="auto"
                  outlined
                />
              </div>
            </v-col>
          </v-row>

          <v-divider color="white" class="mt-2 mb-4" />

          <HCheckbox
            v-model="payment.usePreviousContract"
            :label="$t('financial.updatePayments.label.useOriginalContract')"
          />
        </v-card>

      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        class="mr-5"
        elevation="0"
        large
        text
      >
        {{ $t('create.cancel') }}
      </v-btn>

      <v-btn
        @click="save"
        :loading="isLoading"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('create.generateCalculatedPayment') }}
      </v-btn>
    </template>
  </Modal>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { checkCurrencyDecimals, clearCurrency } from '@/util/helpers'
import { required } from '@/util/errorHandling'
import { VMoney } from 'v-money'

import Modal from '@/components/app/Modal'
import HCheckbox from '@/components/app/HCheckbox'
import HAutocomplete from '@/components/app/HAutocomplete'
import { catalogCurrency } from '../../util/catalog'

export default {
  name: 'CalculatedPayment',

  components: {
    HAutocomplete,
    HCheckbox,
    Modal,
  },

  directives: {
    money: VMoney
  },

  props: {
    appointmentId: {
      required: true,
    },
  },

  computed: {
    ...mapGetters([
      'appointment',
      'recalculateDialog',
      'appointmentItemReasons',
    ]),

    hasAnyRealtyDone () {
      return (this.payment.realties || []).some(r => r.done)
    },
  },

  watch: {
    hasAnyRealtyDone (val) {
      if (!val) this.payment.movementDescription = null
    }
  },

  methods: {
    ...mapActions(['listAppointmentItemReason', 'createCalculatedPayment']),
    ...mapMutations(['closeAllModals']),

    preparePayment() {
      this.payment =  {
        usePreviousContract: false,
        additionals: [],
        realties: [],
        movementDescription: null,
      }

      for (var additional of this.appointment.additionals) {
        this.payment.additionals.push({
          id: additional.id,
          description: additional.description,
          value: checkCurrencyDecimals(additional.value),
        })
      }

      for (var realty of this.appointment.realties) {
        this.payment.realties.push({
          id: realty.id,
          code: realty.code,
          address: realty.address,
          done: false,
        })
      }
    },

    save() {
      if (!this.$refs.calculateForm.validate()) return

      this.isLoading = true

      Promise.resolve(this.buildRequestBody())
        .then((bodyResponse) => this.createCalculatedPayment(bodyResponse))
        .finally(() => (this.isLoading = false))
    },

    async buildRequestBody() {
      var body = {
        appointment_id: this.appointmentId,
        use_previous_contract: this.payment.usePreviousContract,
        update_item_status_request_list: [],
        additional: {},
        movement_comment: this.hasAnyRealtyDone ? (this.payment.movementDescription || null) : null,
      }

      const { additionals, realties } = this.payment

      if (additionals) {
        for(var additional of additionals) {
          body.additional[additional.id] = clearCurrency(additional.value)
        }
      }

      if (realties) {
        for await (const realty of realties) {
          const itemStatusRequest = {
            realty_id: realty.id,
            done: realty.done,
            reason_id: realty.itemReason,
            description: realty.description,
          }

          body.update_item_status_request_list.push(itemStatusRequest)
        }
      }

      return body
    },
  },

  data() {
    return {
      body: null,
      isValid: false,
      isLoading: false,
      enabledToSave: false,
      changeAdditional: false,
      payment: {
        usePreviousContract: false,
        additionals: [],
        realties: [],
        movementDescription: null,
      },

      // currency,
      catalogCurrency,
      clearCurrency,

      rules: {
        required,
      }
    }
  },

  mounted() {
    this.payment =  {
      usePreviousContract: false,
      additionals: [],
      realties: [],
      movementDescription: null,
    }

    this.listAppointmentItemReason()
  },
}
</script>
